import React, { useEffect, useState } from "react";
import { Spinner, Row, Col, Card, Badge, Alert as BsAlert, ProgressBar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SafetyDashboardService from "../../services/safety/SafetyDashboardService";
import AgenziaSelector, { useSafetyAgenziaContext } from "../../components/safety/AgenziaSelector/AgenziaSelector";

/**
 * SafetyHubPage — "Centro Adempimenti Sicurezza"
 *
 * Pagina-guida (forma guidata) del modulo GAS. Organizza tutti gli
 * adempimenti D.Lgs. 81/08 in:
 *  - un "percorso di conformità" passo-passo con stato reale (da KPI);
 *  - una mappa di tutte le aree del modulo (attive e in arrivo).
 *
 * Le aree "in arrivo" diventano cliccabili man mano che i relativi
 * domini vengono rilasciati.
 */

// Mappa colore semaforo -> presentazione
function tonoSemaforo(colore) {
    switch ((colore || "GRAY").toUpperCase()) {
        case "GREEN":  return { variant: "success", label: "In regola",     icon: "bi-check-circle-fill" };
        case "YELLOW": return { variant: "warning", label: "Da verificare", icon: "bi-exclamation-triangle-fill" };
        case "RED":    return { variant: "danger",  label: "Critico",       icon: "bi-x-octagon-fill" };
        default:       return { variant: "secondary", label: "Da iniziare", icon: "bi-circle" };
    }
}

function StepRow({ n, titolo, descrizione, stato, ctaLabel, onClick, disabled }) {
    const t = tonoSemaforo(stato);
    return (
        <div className="d-flex align-items-start gap-3 py-3 border-bottom">
            <div
                className={`d-flex align-items-center justify-content-center rounded-circle bg-${t.variant} text-white flex-shrink-0`}
                style={{ width: 34, height: 34, fontWeight: 600 }}
            >
                {n}
            </div>
            <div className="flex-grow-1">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="fw-semibold">{titolo}</span>
                    <Badge bg={t.variant}>
                        <i className={`bi ${t.icon} me-1`} />
                        {t.label}
                    </Badge>
                </div>
                <div className="text-muted small mt-1">{descrizione}</div>
            </div>
            {ctaLabel && (
                <button
                    className="btn btn-sm btn-outline-primary flex-shrink-0"
                    onClick={onClick}
                    disabled={disabled}
                >
                    {ctaLabel}
                </button>
            )}
        </div>
    );
}

function AreaCard({ icon, title, descrizione, tone = "primary", onClick }) {
    return (
        <Card
            className="border-0 shadow-sm h-100"
            role="button"
            onClick={onClick}
            style={{ cursor: "pointer" }}
        >
            <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div
                        className={`d-flex align-items-center justify-content-center rounded text-${tone}`}
                        style={{ width: 40, height: 40, background: "rgba(13,110,253,.08)" }}
                    >
                        <i className={`bi ${icon} fs-5`} />
                    </div>
                    <Badge bg="success-subtle" text="dark" style={{ background: "#d1e7dd", color: "#0f5132" }}>Attivo</Badge>
                </div>
                <div className="fw-semibold">{title}</div>
                <div className="text-muted small mt-1 flex-grow-1">{descrizione}</div>
                <div className="mt-2">
                    <span className="text-primary small">
                        Apri <i className="bi bi-arrow-right-short" />
                    </span>
                </div>
            </Card.Body>
        </Card>
    );
}

export default function SafetyHubPage() {
    const navigate = useNavigate();
    const { idAgenzia, setIdAgenzia, isManager } = useSafetyAgenziaContext();
    const [loading, setLoading] = useState(true);
    const [kpi, setKpi] = useState(null);
    const [errore, setErrore] = useState(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            setErrore(null);
            try {
                const resp = await SafetyDashboardService.getKpi(idAgenzia);
                const res = resp && typeof resp.json === "function" ? await resp.json() : resp;
                if (mounted) setKpi(res);
            } catch (err) {
                if (mounted) setErrore(err?.message || "Errore nel caricamento dello stato di conformità");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [idAgenzia]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    const indice = Number(kpi?.indice_conformita ?? 0);
    const indiceTone = indice >= 90 ? "success" : indice >= 70 ? "warning" : "danger";
    const sem = kpi?.semafori || {};

    // Stato dei passi della "forma guidata"
    const haAziende = Number(kpi?.aziende_totali ?? 0) > 0;
    const haLavoratori = Number(kpi?.lavoratori_totali ?? 0) > 0;

    const steps = [
        {
            titolo: "1. Anagrafica azienda",
            descrizione: "Censisci i dati dell'azienda, le sedi e la classe di rischio. È il punto di partenza per tutti gli adempimenti.",
            stato: haAziende ? "GREEN" : "GRAY",
            cta: "La mia azienda",
            onClick: () => navigate("/safety/aziende"),
        },
        {
            titolo: "2. Lavoratori e mansioni",
            descrizione: "Inserisci i lavoratori (anche importandoli da eduform) e assegna mansione e inquadramento.",
            stato: haLavoratori ? "GREEN" : (haAziende ? "YELLOW" : "GRAY"),
            cta: "Lavoratori",
            onClick: () => navigate("/safety/lavoratori"),
        },
        {
            titolo: "3. Formazione e attestati",
            descrizione: "Verifica gli attestati 81/08 (sync eduform + caricamento manuale) e tieni sotto controllo le scadenze.",
            stato: sem?.attestati?.colore || "GRAY",
            cta: "Vai ai lavoratori",
            onClick: () => navigate("/safety/lavoratori"),
        },
        {
            titolo: "4. Sorveglianza sanitaria",
            descrizione: "Registra le visite mediche e i giudizi di idoneità (art. 41) con le relative scadenze.",
            stato: sem?.visite?.colore || "GRAY",
            cta: "Vai ai lavoratori",
            onClick: () => navigate("/safety/lavoratori"),
        },
        {
            titolo: "5. Documenti obbligatori",
            descrizione: "Archivia DVR, nomine, designazioni e DPI; il sistema segnala i documenti obbligatori mancanti.",
            stato: sem?.documenti?.colore || "GRAY",
            cta: "Apri documenti",
            onClick: () => navigate("/safety/aziende?tab=documenti"),
        },
        {
            titolo: "6. Nomine e figure della sicurezza",
            descrizione: "RSPP, medico competente, RLS, preposti e addetti emergenze: chi ricopre la figura, interno o esterno, con scadenze aggiornamento.",
            stato: sem?.nomine?.colore || "GRAY",
            cta: "Apri nomine",
            onClick: () => navigate("/safety/aziende?tab=nomine"),
        },
        {
            titolo: "7. Valutazioni dei rischi",
            descrizione: "Valutazioni specifiche integrative al DVR: aggressione/rapina, lavoro notturno, stress L-C, VDT, lavoratore isolato, guida mezzi…",
            stato: sem?.valutazioni?.colore || "GRAY",
            cta: "Apri valutazioni",
            onClick: () => navigate("/safety/aziende?tab=valutazioni"),
        },
        {
            titolo: "8. Autorizzazioni, polizze e certificazioni",
            descrizione: "Autorizzazioni prefettizie (TULPS), regolamento di servizio, polizze RC/INAIL e certificazioni UNI/ISO — titoli distinti, non solo «licenze».",
            stato: sem?.licenze?.colore || "GRAY",
            cta: "Apri titoli aziendali",
            onClick: () => navigate("/safety/aziende?tab=licenze"),
        },
        {
            titolo: "9. Dotazioni e armamento",
            descrizione: "Registro armi (matricola, revisione), giubbotti antiproiettile, radio e dotazioni operative con assegnazione al personale.",
            stato: sem?.dotazioni?.colore || "GRAY",
            cta: "Apri registro dotazioni",
            onClick: () => navigate("/safety/aziende?tab=dotazioni"),
        },
        {
            titolo: "10. Appalti e idoneità imprese",
            descrizione: "Subappaltatori e fornitori critici: DURC, DUVRI, idoneità T-P, patente crediti, POS/PSC con scadenze per commessa o cantiere.",
            stato: sem?.appalti?.colore || "GRAY",
            cta: "Apri appalti",
            onClick: () => navigate("/safety/aziende?tab=appalti"),
        },
        {
            titolo: "11. Attrezzature e verifiche periodiche",
            descrizione: "Censisci macchine/impianti soggetti a verifica (art. 71, DPR 462, antincendio) e registra i controlli periodici.",
            stato: sem?.attrezzature?.colore || "GRAY",
            cta: "Apri attrezzature",
            onClick: () => navigate("/safety/aziende?tab=attrezzature"),
        },
        {
            titolo: "12. Infortuni e near miss",
            descrizione: "Registro eventi, denunce INAIL, giorni di assenza e indici di frequenza/gravità (IF/IG) per l'anno in corso.",
            stato: sem?.infortuni?.colore || "GRAY",
            cta: "Apri registro infortuni",
            onClick: () => navigate("/safety/aziende?tab=infortuni"),
        },
        {
            titolo: "13. Riunione periodica (art. 35)",
            descrizione: "Convocazione e verbale della riunione annuale obbligatoria per aziende con almeno 15 lavoratori.",
            stato: sem?.riunioni?.colore || "GRAY",
            cta: "Apri riunioni periodiche",
            onClick: () => navigate("/safety/aziende?tab=riunioni"),
        },
        {
            titolo: "14. Sopralluoghi e non conformità",
            descrizione: "Audit interni, rilievi ispettivi e registro NC con azioni correttive, responsabili e scadenze.",
            stato: sem?.sopralluoghi?.colore || "GRAY",
            cta: "Apri sopralluoghi",
            onClick: () => navigate("/safety/aziende?tab=sopralluoghi"),
        },
        {
            titolo: "15. DPI per mansione",
            descrizione: "Matrice DPI obbligatori per mansione e registro consegne ai lavoratori con ricevuta/firma.",
            stato: sem?.dpi?.colore || "GRAY",
            cta: "Apri DPI",
            onClick: () => navigate("/safety/aziende?tab=dpi"),
        },
        {
            titolo: "16. Lavoratori particolari",
            descrizione: "Gestanti, minori, lavoro notturno, isolato e disabilità con valutazioni dedicate e revisioni.",
            stato: sem?.particolari?.colore || "GRAY",
            cta: "Apri particolari",
            onClick: () => navigate("/safety/aziende?tab=particolari"),
        },
        {
            titolo: "17. Scadenzario e notifiche",
            descrizione: "Scadenzario unificato cross-dominio con filtri e promemoria email configurabili per agenzia.",
            stato: sem?.scadenzario?.colore || "GRAY",
            cta: "Apri scadenzario",
            onClick: () => navigate("/safety/scadenzario"),
        },
        {
            titolo: "18. Area ispettiva",
            descrizione: "Checklist pronto-verifica, verbali delle ispezioni, prescrizioni con scadenze e fascicolo esportabile.",
            stato: sem?.ispettiva?.colore || "GRAY",
            cta: "Apri area ispettiva",
            onClick: () => navigate("/safety/aziende?tab=ispettiva"),
        },
    ];

    // Aree del modulo
    const areeAttive = [
        { icon: "bi-speedometer2", title: "Dashboard compliance", descrizione: "KPI, semafori e indice di conformità complessivo.", onClick: () => navigate("/safety/dashboard") },
        { icon: "bi-building", title: "La mia azienda", descrizione: "Dati azienda, sedi, classe di rischio e documenti aziendali.", onClick: () => navigate("/safety/aziende") },
        { icon: "bi-people", title: "Lavoratori", descrizione: "Anagrafiche, formazione, sorveglianza sanitaria e documenti del lavoratore.", onClick: () => navigate("/safety/lavoratori") },
        { icon: "bi-patch-check", title: "Abilitazioni e titoli del personale", descrizione: "Decreto GPG, porto d'armi, idoneità maneggio armi, tessera di riconoscimento e patenti, con scadenze.", onClick: () => navigate("/safety/lavoratori") },
        { icon: "bi-person-badge", title: "Nomine e figure della sicurezza", descrizione: "RSPP, medico competente, RLS, preposti e addetti emergenze con scadenze aggiornamento.", onClick: () => navigate("/safety/aziende?tab=nomine") },
        { icon: "bi-clipboard-data", title: "Valutazioni dei rischi", descrizione: "Aggressione/rapina, notturno, stress L-C, VDT, isolato, guida mezzi — revisioni periodiche.", onClick: () => navigate("/safety/aziende?tab=valutazioni") },
        { icon: "bi-award", title: "Autorizzazioni, polizze e certificazioni", descrizione: "Art. 134/138 TULPS, regolamento di servizio, polizze RC/INAIL e certificazioni UNI/ISO.", onClick: () => navigate("/safety/aziende?tab=licenze") },
        { icon: "bi-shield-shaded", title: "Dotazioni e armamento", descrizione: "Registro armi, giubbotti antiproiettile, radio e dotazioni operative con scadenze revisione/validità.", onClick: () => navigate("/safety/aziende?tab=dotazioni") },
        { icon: "bi-briefcase", title: "Appalti e idoneità imprese", descrizione: "DURC, DUVRI, idoneità T-P e patente crediti di subappaltatori e fornitori critici.", onClick: () => navigate("/safety/aziende?tab=appalti") },
        { icon: "bi-tools", title: "Attrezzature e verifiche periodiche", descrizione: "Macchine/impianti soggetti a verifica (art. 71, DPR 462) e presìdi antincendio con storico controlli.", onClick: () => navigate("/safety/aziende?tab=attrezzature") },
        { icon: "bi-bandaid", title: "Infortuni e near miss", descrizione: "Registro eventi, comunicazioni INAIL e indici di frequenza/gravità.", onClick: () => navigate("/safety/aziende?tab=infortuni") },
        { icon: "bi-calendar-check", title: "Riunione periodica (art. 35)", descrizione: "Convocazioni e verbali della riunione annuale (aziende ≥ 15 lavoratori).", onClick: () => navigate("/safety/aziende?tab=riunioni") },
        { icon: "bi-search", title: "Sopralluoghi e non conformità", descrizione: "Audit interni, rilievi e azioni correttive con responsabili e scadenze.", onClick: () => navigate("/safety/aziende?tab=sopralluoghi") },
        { icon: "bi-shield", title: "DPI per mansione", descrizione: "Matrice DPI per mansione e registro consegne ai lavoratori.", onClick: () => navigate("/safety/aziende?tab=dpi") },
        { icon: "bi-person-hearts", title: "Lavoratori particolari", descrizione: "Gestanti, minori e lavoro notturno con valutazioni dedicate.", onClick: () => navigate("/safety/aziende?tab=particolari") },
        { icon: "bi-bell", title: "Scadenzario e notifiche", descrizione: "Scadenzario unico con avvisi email configurabili.", onClick: () => navigate("/safety/scadenzario") },
        { icon: "bi-folder-symlink", title: "Area ispettiva", descrizione: "Checklist pronto-verifica, verbali e fascicolo esportabile.", onClick: () => navigate("/safety/aziende?tab=ispettiva") },
    ];
    if (isManager) {
        areeAttive.push({ icon: "bi-diagram-3", title: "Mappatura corsi 81", descrizione: "Associa i corsi della piattaforma alle tipologie normative 81/08.", onClick: () => navigate("/safety/mappatura-81") });
    }

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <i className="bi bi-compass me-2 text-success" />
                        Centro Adempimenti Sicurezza
                    </h4>
                    <div className="text-muted small">
                        Il percorso guidato per la compliance D.Lgs. 81/2008
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <AgenziaSelector idAgenzia={idAgenzia} onChange={setIdAgenzia} />
                    <Badge bg={indiceTone}>Indice conformità: {indice}%</Badge>
                </div>
            </div>

            {errore && <BsAlert variant="warning" className="small">{errore}</BsAlert>}

            <Row className="g-3">
                {/* Percorso guidato */}
                <Col xs={12} lg={7}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <h6 className="mb-0">
                                    <i className="bi bi-signpost-2 me-2 text-primary" />
                                    Percorso di conformità
                                </h6>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/safety/dashboard")}>
                                    Apri dashboard
                                </button>
                            </div>
                            <div className="mb-2">
                                <ProgressBar
                                    now={indice}
                                    variant={indiceTone}
                                    label={`${indice}%`}
                                    style={{ height: 18 }}
                                />
                            </div>
                            {steps.map((s, i) => (
                                <StepRow
                                    key={i}
                                    n={i + 1}
                                    titolo={s.titolo}
                                    descrizione={s.descrizione}
                                    stato={s.stato}
                                    ctaLabel={s.cta}
                                    onClick={s.onClick}
                                />
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Riepilogo rapido */}
                <Col xs={12} lg={5}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body>
                            <h6 className="mb-3">
                                <i className="bi bi-clipboard-check me-2 text-primary" />
                                Cosa richiede attenzione
                            </h6>
                            <div className="d-flex flex-column gap-2">
                                <RiepilogoRiga
                                    label="Documenti obbligatori mancanti"
                                    value={kpi?.documenti_mancanti ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=documenti")}
                                />
                                <RiepilogoRiga
                                    label="Attestati scaduti"
                                    value={kpi?.attestati_scaduti ?? 0}
                                    onClick={() => navigate("/safety/lavoratori")}
                                />
                                <RiepilogoRiga
                                    label="Attestati in scadenza"
                                    value={kpi?.attestati_in_scadenza ?? 0}
                                    tone="warning"
                                    onClick={() => navigate("/safety/lavoratori")}
                                />
                                <RiepilogoRiga
                                    label="Visite mediche scadute"
                                    value={kpi?.visite_scadute ?? 0}
                                    onClick={() => navigate("/safety/lavoratori")}
                                />
                                <RiepilogoRiga
                                    label="Valutazioni obbligatorie mancanti"
                                    value={kpi?.valutazioni_mancanti ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=valutazioni")}
                                />
                                <RiepilogoRiga
                                    label="Revisioni valutazioni scadute"
                                    value={kpi?.valutazioni_scadute ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=valutazioni")}
                                />
                                <RiepilogoRiga
                                    label="Titoli obbligatori mancanti"
                                    value={kpi?.licenze_mancanti ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=licenze")}
                                />
                                <RiepilogoRiga
                                    label="Titoli aziendali scaduti"
                                    value={kpi?.licenze_scadute ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=licenze")}
                                />
                                <RiepilogoRiga
                                    label="Dotazioni in scadenza"
                                    value={kpi?.dotazioni_in_scadenza ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=dotazioni")}
                                />
                                <RiepilogoRiga
                                    label="Dotazioni scadute"
                                    value={kpi?.dotazioni_scadute ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=dotazioni")}
                                />
                                <RiepilogoRiga
                                    label="Verifiche attrezzature scadute"
                                    value={kpi?.verifiche_scadute ?? 0}
                                    onClick={() => navigate("/safety/aziende?tab=attrezzature")}
                                />
                                <RiepilogoRiga
                                    label="Documenti in scadenza"
                                    value={kpi?.documenti_in_scadenza ?? 0}
                                    tone="warning"
                                    onClick={() => navigate("/safety/aziende?tab=documenti")}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Mappa delle aree */}
            <h6 className="mt-4 mb-2">
                <i className="bi bi-grid me-2 text-primary" />
                Aree del modulo
            </h6>
            <Row className="g-3">
                {areeAttive.map((a, i) => (
                    <Col xs={12} sm={6} lg={3} key={`att-${i}`}>
                        <AreaCard {...a} />
                    </Col>
                ))}
            </Row>
        </div>
    );
}

function RiepilogoRiga({ label, value, tone, onClick }) {
    const n = Number(value || 0);
    const badgeTone = n === 0 ? "success" : (tone || "danger");
    return (
        <div
            className="d-flex align-items-center justify-content-between p-2 rounded"
            role="button"
            onClick={onClick}
            style={{ background: "rgba(0,0,0,.02)" }}
        >
            <span className="small">{label}</span>
            <Badge bg={badgeTone}>{n}</Badge>
        </div>
    );
}
